import PropTypes from 'prop-types';
import Iconify from 'src/components/Iconify';
import { TEMPLATES_MESSENGER_DESIGN as D } from './TemplatesMessenger.design';
import { PLACEHOLDER_REGEX } from './templatesMessenger.utils';

/**
 * Mockup de celular com o preview da utility message.
 *
 * Duas formas, seguindo o que a Conversation API realmente devolve:
 *  - só BODY            -> balão de texto azul comum
 *  - HEADER ou BUTTONS  -> generic template (card branco com título, subtítulo e botões)
 * ref: .../send-messages/utility-messages#utility-messages-in-conversation-api
 */

// Substitui as variáveis pelos valores de exemplo, ou as destaca quando não há valor.
function TextoComVariaveis({ texto, valores = {}, classeDestaque }) {
    if (!texto) return null;

    const partes = [];
    let ultimoIndice = 0;

    for (const match of String(texto).matchAll(PLACEHOLDER_REGEX)) {
        if (match.index > ultimoIndice) {
            partes.push({ tipo: 'texto', valor: texto.slice(ultimoIndice, match.index) });
        }
        const chave = match[1];
        partes.push({ tipo: 'variavel', chave, valor: valores[chave] });
        ultimoIndice = match.index + match[0].length;
    }
    if (ultimoIndice < texto.length) {
        partes.push({ tipo: 'texto', valor: texto.slice(ultimoIndice) });
    }

    return (
        <>
            {partes.map((parte, i) => {
                if (parte.tipo === 'texto') return <span key={i}>{parte.valor}</span>;
                const preenchido = parte.valor !== undefined && parte.valor !== null && String(parte.valor).trim() !== '';
                return (
                    <span key={i} className={classeDestaque}>
                        {preenchido ? parte.valor : `{{${parte.chave}}}`}
                    </span>
                );
            })}
        </>
    );
}

TextoComVariaveis.propTypes = {
    texto: PropTypes.string,
    valores: PropTypes.object,
    classeDestaque: PropTypes.string
};

export default function MessengerPreview({
    pageName = 'Sua Página',
    pageProfile,
    headerType = 'none',
    headerText = '',
    headerImageUrl = '',
    body = '',
    buttons = [],
    valores = {},
    // No formato posicional o {{1}} do título e o {{1}} do corpo são parâmetros
    // DIFERENTES para a Meta. Quem preenche os dois separadamente passa este mapa;
    // quem só tem um conjunto (drawer de criação) continua passando `valores`.
    valoresHeader = null,
    compacto = false
}) {
    const valoresDoHeader = valoresHeader || valores;
    const temHeader = headerType === 'TEXT' ? !!headerText?.trim() : headerType === 'IMAGE';
    const temBotoes = (buttons || []).length > 0;
    // Balão simples só quando não há header nem botões
    const comoCard = temHeader || temBotoes;

    const conteudo = comoCard ? (
        <div className={D.drawer.preview.cardTemplate}>
            {headerType === 'IMAGE' && (
                headerImageUrl
                    ? <img src={headerImageUrl} alt="" className={D.drawer.preview.cardImage} />
                    : (
                        <div className={`${D.drawer.preview.cardImage} flex items-center justify-center`}>
                            <Iconify icon="eva:image-outline" width={28} className="text-gray-300" />
                        </div>
                    )
            )}

            <div className={D.drawer.preview.cardBody}>
                {headerType === 'TEXT' && headerText?.trim() && (
                    <p className={D.drawer.preview.cardTitle}>
                        <TextoComVariaveis texto={headerText} valores={valoresDoHeader} classeDestaque={D.drawer.preview.variavelNoCard} />
                    </p>
                )}
                {body?.trim() && (
                    <p className={D.drawer.preview.cardSubtitle}>
                        <TextoComVariaveis texto={body} valores={valores} classeDestaque={D.drawer.preview.variavelNoCard} />
                    </p>
                )}
                {!body?.trim() && !headerText?.trim() && (
                    <p className="text-[12px] text-gray-400 italic">A mensagem aparece aqui…</p>
                )}
            </div>

            {temBotoes && (
                <div>
                    {buttons.map((btn, i) => (
                        <button key={i} type="button" className={D.drawer.preview.cardButton}>
                            {btn.text || 'Botão'}
                        </button>
                    ))}
                </div>
            )}
        </div>
    ) : (
        <div className={D.drawer.preview.messageBubble}>
            <p className="text-[12px] leading-snug whitespace-pre-wrap">
                {body?.trim()
                    ? <TextoComVariaveis texto={body} valores={valores} classeDestaque={D.drawer.preview.variavelNaBolha} />
                    : <span className="text-white/70 italic">A mensagem aparece aqui…</span>}
            </p>
            <div className="flex items-center justify-end gap-1 mt-1">
                <span className="text-[10px] text-white/70">agora</span>
                <Iconify icon="eva:checkmark-circle-2-fill" width={11} className="text-white/70" />
            </div>
        </div>
    );

    // Versão sem moldura, usada nos cards da biblioteca
    if (compacto) {
        return <div className="bg-white p-2.5 rounded-lg">{conteudo}</div>;
    }

    return (
        <div className={D.drawer.preview.phoneFrame}>
            <div className={D.drawer.preview.phoneInner}>
                <div className={D.drawer.preview.notch}>
                    <div className={D.drawer.preview.notchPill} />
                </div>

                {/* Header do Messenger: branco, nome em preto */}
                <div className={D.drawer.preview.messengerHeader}>
                    <Iconify icon="eva:arrow-ios-back-outline" width={18} className="text-[#0084FF]" />
                    {pageProfile
                        ? <img src={pageProfile} alt="" className="w-7 h-7 rounded-full object-cover" />
                        : (
                            <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center">
                                {/* Glifo inline de propósito, NÃO <Iconify icon="logos:messenger" />.
                                    Ícones multicoloridos do Iconify têm <defs> com ids gerados a cada
                                    render, então ele remove e recria os nós SVG toda vez. Dentro de um
                                    Dialog do Radix o MutationObserver do FocusScope lê essas remoções
                                    como "o elemento focado sumiu" e devolve o foco ao container — o que
                                    torna impossível focar os inputs do diálogo com o mouse
                                    (era o bug dos campos de variável travados). */}
                                <svg viewBox="0 0 24 24" width="15" height="15" fill="#0084FF" aria-hidden="true">
                                    <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.301 2.246.464 3.443.464 6.627 0 12-4.974 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.13 3.26L19.752 8l-6.561 6.963z" />
                                </svg>
                            </div>
                        )}
                    <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-gray-900 truncate">{pageName}</p>
                        <p className="text-[9px] text-gray-400">Messenger</p>
                    </div>
                    <Iconify icon="eva:video-outline" width={16} className="text-[#0084FF]" />
                    <Iconify icon="eva:info-outline" width={16} className="text-[#0084FF]" />
                </div>

                <div className={D.drawer.preview.chatArea}>{conteudo}</div>

                <div className={D.drawer.preview.inputBar}>
                    <Iconify icon="eva:plus-circle-outline" width={16} className="text-[#0084FF]" />
                    <div className="flex-1 bg-white rounded-full px-3 py-1">
                        <span className="text-[10px] text-gray-400">Mensagem</span>
                    </div>
                    <Iconify icon="eva:smiling-face-outline" width={16} className="text-[#0084FF]" />
                </div>

                <div className={D.drawer.preview.homeIndicator}>
                    <div className="w-28 h-1 bg-gray-700 rounded-full" />
                </div>
            </div>
        </div>
    );
}

MessengerPreview.propTypes = {
    pageName: PropTypes.string,
    pageProfile: PropTypes.string,
    headerType: PropTypes.oneOf(['none', 'TEXT', 'IMAGE']),
    headerText: PropTypes.string,
    headerImageUrl: PropTypes.string,
    body: PropTypes.string,
    buttons: PropTypes.array,
    valores: PropTypes.object,
    valoresHeader: PropTypes.object,
    compacto: PropTypes.bool
};
